import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { typography, radius } from '@/shared/theme/tokens';
import { REC_ACCENT, REC_IMPACT_META, OPEN_LOOP_META } from './consensusMeta';

// How many ranked recs the ambient surface shows before "+N more". A passive
// wall display stays glance-resumable: stable priority order, never a carousel.
const VISIBLE = 4;

/**
 * Right-region recommendation stack: a static, priority-ranked list of the recs
 * that still need an operator decision. Replaces the old auto-rotating ticker —
 * rotation makes the screen state timing-dependent (two operators glancing
 * seconds apart would see different priorities). Shows NO confidence score
 * (product decision); conviction is carried by impact + time-to-impact + basis.
 */
export function RecommendationTicker({ recs = [], paused = false, animate, onSelect }) {
  const c = useThemeColors();
  const isDark = useIsDark();
  const shown = recs.slice(0, VISIBLE);
  const overflow = recs.length - shown.length;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1,
      borderRadius: radius['2xl'], border: `1px solid ${c.border}`, background: c.bg, overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
        borderBottom: `1px solid ${c.border}`, flexShrink: 0,
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 18, color: REC_ACCENT }}>lightbulb</span>
        <span style={{ fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.bold, color: c.textPrimary }}>
          Recommendations
        </span>
        <span style={{ marginLeft: 'auto', fontSize: typography.fontSize.sm, color: c.textSubtle, fontVariantNumeric: 'tabular-nums' }}>
          {recs.length}
        </span>
      </div>

      <div style={{ flex: 1, minHeight: 0, padding: 12, display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto' }}>
        {paused ? (
          <Centered c={c} icon="sensors_off" color={OPEN_LOOP_META.color}
            text="Recommendations paused" sub="Live data is stale — advisory held until inputs recover" />
        ) : shown.length === 0 ? (
          <Centered c={c} icon="check_circle" color="#16a34a"
            text="No actions waiting" sub="Nothing needs an operator decision right now" />
        ) : (
          <>
            {shown.map((rec, i) => (
              <RecCard key={rec.id} rec={rec} top={i === 0} c={c} isDark={isDark} animate={animate} onSelect={onSelect} />
            ))}
            {overflow > 0 && (
              <div style={{
                textAlign: 'center', fontSize: typography.fontSize.sm, color: c.textSubtle,
                padding: '4px 0 2px', fontVariantNumeric: 'tabular-nums',
              }}>
                +{overflow} more in queue
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Centered({ c, icon, color, text, sub }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, textAlign: 'center', padding: '20px 16px' }}>
      <span className="material-symbols-outlined" style={{ fontSize: 30, color }}>{icon}</span>
      <span style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, color: c.textPrimary }}>{text}</span>
      <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle, maxWidth: 240 }}>{sub}</span>
    </div>
  );
}

function RecCard({ rec, top, c, isDark, animate, onSelect }) {
  const impact = REC_IMPACT_META[rec.impact] ?? REC_IMPACT_META.low;
  const numerical = rec.action?.type === 'numerical';
  const clickable = typeof onSelect === 'function';
  const openLoop = rec.status === 'accepted';
  const seenBefore = rec.previousSameCount > 0;

  return (
    <div
      onClick={clickable ? () => onSelect(rec) : undefined}
      onKeyDown={clickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(rec); }
      } : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      title={clickable ? `Open ${rec.parameterName} recommendation` : undefined}
      style={{
        display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0,
        padding: top ? '14px 14px' : '11px 12px',
        borderRadius: radius.xl,
        cursor: clickable ? 'pointer' : 'default',
        background: top ? (isDark ? 'rgba(245,158,11,0.06)' : 'rgba(245,158,11,0.05)') : c.bgSubtle,
        border: `1px solid ${top ? 'rgba(245,158,11,0.35)' : c.borderSubtle}`,
        borderLeft: `4px solid ${REC_ACCENT}`,
        animation: animate ? 'lm-fade-in 0.4s ease' : 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontFamily: typography.fontFamily.heading, fontSize: top ? typography.fontSize.xl : typography.fontSize.lg, fontWeight: typography.fontWeight.bold, color: c.textPrimary, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {rec.parameterName}
        </span>
        <span style={{
          fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: impact.color,
          background: isDark ? impact.bgDark : impact.bgLight,
          borderRadius: radius.full, padding: '3px 9px', textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wide, flexShrink: 0,
        }}>
          {impact.label}
        </span>
      </div>

      {/* Action */}
      {numerical ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <ValueBlock label="Current" value={rec.action.currentValue} unit={rec.unit} color={c.textMuted} big={top} c={c} />
          <span className="material-symbols-outlined" style={{ fontSize: top ? 24 : 20, color: REC_ACCENT }}>trending_flat</span>
          <ValueBlock label="Recommended" value={rec.action.recommendedValue} unit={rec.unit} color={c.textPrimary} strong big={top} c={c} />
        </div>
      ) : (
        <div style={{
          fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, color: c.textPrimary, lineHeight: 1.4,
          padding: '8px 10px', borderRadius: radius.md, background: c.bgMuted, borderLeft: `3px solid ${REC_ACCENT}`,
        }}>
          {rec.action?.instruction}
        </div>
      )}

      {/* Expected impact (top card only, to keep the stack scannable) */}
      {top && (
        <div style={{ fontSize: typography.fontSize.sm, color: c.textSecondary, lineHeight: 1.5 }}>
          {rec.expectedImpact}
        </div>
      )}

      {/* Footer: time-to-impact + trust basis + open-loop state */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', color: c.textSubtle, fontSize: typography.fontSize.sm }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 15 }}>schedule</span>
          Impact in {rec.timeToImpact}
        </span>
        {seenBefore && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }} title="Basis: this action has been taken before in comparable states">
            <span className="material-symbols-outlined" style={{ fontSize: 15 }}>history</span>
            Seen {rec.previousSameCount}× before
          </span>
        )}
        {openLoop && (
          <span style={{
            marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5,
            fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: OPEN_LOOP_META.color,
            background: isDark ? OPEN_LOOP_META.bgDark : OPEN_LOOP_META.bgLight,
            border: `1px solid ${OPEN_LOOP_META.border}`, borderRadius: radius.full, padding: '2px 9px',
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 13 }}>hourglass_top</span>
            {OPEN_LOOP_META.label}
          </span>
        )}
      </div>
    </div>
  );
}

function ValueBlock({ label, value, unit, color, strong, big, c }) {
  const size = strong
    ? (big ? typography.fontSize['4xl'] : typography.fontSize['3xl'])
    : (big ? typography.fontSize['3xl'] : typography.fontSize['2xl']);
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <span style={{ fontSize: typography.fontSize.xs, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wide }}>
        {label}
      </span>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
        <span style={{ fontSize: size, fontWeight: typography.fontWeight.bold, color, fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </span>
        {unit && <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle }}>{unit}</span>}
      </div>
    </div>
  );
}
